import { LayoutInternal } from '$/views/emails/layout.tsx';
import BrandedSimple from '@/layouts/BrandedSimple/BrandedSimple.tsx';
import Default from '@/layouts/Default/Default.tsx';
import MainLayout from '@/layouts/MainLayout/MainLayout.tsx';
import SimpleCenter from '@/layouts/SimpleCenter/SimpleCenter.tsx';
import { transformRouterConfig } from '@/router/transformRouter.tsx';
import { COMPARE_ENABLED } from '@/config/features';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Loading } from 'react-daisyui';
import Routes from './Routes.ts';

// Page components — all lazy-loaded for route-based code splitting
const AdminCreateAccount         = lazy(() => import('@/pages/AdminCreateAccount.tsx'));
const AdminCreateFullListing     = lazy(() => import('@/pages/AdminCreateFullListing.tsx'));
const AdminGymGoers              = lazy(() => import('@/pages/AdminGymGoers.tsx'));
const AdminAuditLogs             = lazy(() => import('@/pages/AdminAuditLogs.tsx'));
const AdminGymOwners             = lazy(() => import('@/pages/AdminGymOwners.tsx'));
const AdminSubscriptions         = lazy(() => import('@/pages/AdminSubscriptions.tsx'));
const AuthCallbackPage           = lazy(() => import('@/pages/CallbackPage.tsx'));
const CompareScreen              = lazy(() => import('@/pages/CompareScreen.tsx').then(m => ({ default: m.CompareScreen })));
const ConfirmEmailPage           = lazy(() => import('@/pages/ConfirmEmailPage.tsx'));
const EmailChangedConfirmationPage = lazy(() => import('@/pages/EmailChangedConfirmationPage.tsx'));
const ForgotPasswordPage         = lazy(() => import('@/pages/ForgotPasswordPage.tsx'));
const GymDetails                 = lazy(() => import('@/pages/GymDetails.tsx').then(m => ({ default: m.GymDetails })));
const HomePage                   = lazy(() => import('@/pages/HomePage.tsx'));
const LandingPage                = lazy(() => import('@/pages/LandingPage.tsx'));
const LoginPage                  = lazy(() => import('@/pages/LoginPage.tsx'));
const ManageListing              = lazy(() => import('@/pages/ManageListing.tsx'));
const OwnerListingStatus         = lazy(() => import('@/pages/OwnerListingStatus.tsx'));
const OwnerOnboarding            = lazy(() => import('@/pages/OwnerOnboarding.tsx'));
const OwnerListingPreview        = lazy(() => import('@/pages/OwnerListingPreview.tsx'));
const OwnerSubscription          = lazy(() => import('@/pages/OwnerSubscription.tsx'));
const PasswordChangedConfirmationPage = lazy(() => import('@/pages/PasswordChangedConfirmationPage.tsx'));
const PaymentSelectPage          = lazy(() => import('@/pages/PaymentSelectPage.tsx'));
const AssistedListingConfirmation = lazy(() => import('@/pages/AssistedListingConfirmation.tsx'));
const OwnerRegisterPage          = lazy(() => import('@/pages/OwnerRegisterPage.tsx'));
const RegisterPage               = lazy(() => import('@/pages/RegisterPage.tsx'));
const ResetPasswordPage          = lazy(() => import('@/pages/ResetPasswordPage.tsx'));
const Subscription               = lazy(() => import('@/pages/Subscription.tsx'));
const GoerSavedGyms              = lazy(() => import('@/pages/GoerSavedGyms.tsx'));
const GoerDayPassBookings        = lazy(() => import('@/pages/GoerDayPassBookings.tsx'));
const OwnerDayPassBookings       = lazy(() => import('@/pages/OwnerDayPassBookings.tsx'));

const fallback = (
  <div className="flex justify-center py-12">
    <Loading />
  </div>
);

const router = createBrowserRouter(
  transformRouterConfig([
    {
      path: Routes.LANDING,
      element: <Suspense fallback={fallback}><LandingPage /></Suspense>,
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        {
          path: Routes.GYMS,
          element: <Suspense fallback={fallback}><HomePage /></Suspense>,
        },
        {
          path: Routes.GYM_DETAILS,
          element: <Suspense fallback={fallback}><GymDetails /></Suspense>,
        },
        {
          path: Routes.GYMS_COMPARE,
          element: COMPARE_ENABLED
            ? <Suspense fallback={fallback}><CompareScreen /></Suspense>
            : <Navigate to="/" replace />,
        },
      ],
    },
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['admin'],
      children: [
        {
          path: Routes.ADMIN_GYM_OWNERS,
          element: <Suspense fallback={fallback}><AdminGymOwners /></Suspense>,
        },
        {
          path: Routes.ADMIN_GYM_GOERS,
          element: <Suspense fallback={fallback}><AdminGymGoers /></Suspense>,
        },
        {
          path: Routes.ADMIN_SUBSCRIPTIONS,
          element: <Suspense fallback={fallback}><AdminSubscriptions /></Suspense>,
        },
        {
          path: Routes.ADMIN_CREATE_FULL_LISTING,
          element: <Suspense fallback={fallback}><AdminCreateFullListing /></Suspense>,
        },
        {
          path: Routes.ADMIN_EDIT_FULL_LISTING,
          element: <Suspense fallback={fallback}><AdminCreateFullListing /></Suspense>,
        },
        {
          path: Routes.ADMIN_CREATE_ACCOUNT,
          element: <Suspense fallback={fallback}><AdminCreateAccount /></Suspense>,
        },
        {
          path: Routes.ADMIN_AUDIT_LOGS,
          element: <Suspense fallback={fallback}><AdminAuditLogs /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.OWNER_LISTING_STATUS,
          element: <Suspense fallback={fallback}><OwnerListingStatus /></Suspense>,
        },
        {
          path: Routes.OWNER_PROFILE_VISITS,
          element: <Navigate to={Routes.OWNER_LISTING_STATUS} replace />,
        },
        {
          path: Routes.OWNER_SUBSCRIPTION,
          element: <Suspense fallback={fallback}><OwnerSubscription /></Suspense>,
        },
        {
          path: Routes.OWNER_DAY_PASS_BOOKINGS,
          element: <Suspense fallback={fallback}><OwnerDayPassBookings /></Suspense>,
        },
        {
          path: Routes.OWNER_PREVIEW,
          element: <Suspense fallback={fallback}><OwnerListingPreview /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.GOER_DASHBOARD,
          element: <Navigate to={Routes.GOER_SAVED_GYMS} replace />,
        },
        {
          path: Routes.GOER_SAVED_GYMS,
          element: <Suspense fallback={fallback}><GoerSavedGyms /></Suspense>,
        },
        {
          path: Routes.GOER_DAY_PASS_BOOKINGS,
          element: <Suspense fallback={fallback}><GoerDayPassBookings /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.OWNER_ONBOARDING,
          element: <Suspense fallback={fallback}><OwnerOnboarding /></Suspense>,
        },
        {
          path: Routes.SUBSCRIPTION,
          element: <Suspense fallback={fallback}><Subscription /></Suspense>,
        },
        {
          path: Routes.MANAGE_LISTING,
          element: <Suspense fallback={fallback}><ManageListing /></Suspense>,
        },
        {
          path: Routes.ASSISTED_LISTING_CONFIRMATION,
          element: <Suspense fallback={fallback}><AssistedListingConfirmation /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.CALLBACK_AUTH,
          element: <Suspense fallback={fallback}><AuthCallbackPage /></Suspense>,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <Suspense fallback={fallback}><ConfirmEmailPage /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['notLoggedIn'],
      children: [
        {
          path: Routes.FORGOT_PASSWORD,
          element: <Suspense fallback={fallback}><ForgotPasswordPage /></Suspense>,
        },
        {
          path: Routes.LOGIN,
          element: <Suspense fallback={fallback}><LoginPage /></Suspense>,
        },
        {
          path: Routes.REGISTER,
          element: <Suspense fallback={fallback}><RegisterPage /></Suspense>,
        },
        {
          path: Routes.REGISTER_OWNER,
          element: <Suspense fallback={fallback}><OwnerRegisterPage /></Suspense>,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <Suspense fallback={fallback}><ConfirmEmailPage /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.RESET_PASSWORD,
          element: <Suspense fallback={fallback}><ResetPasswordPage /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <BrandedSimple />,
      children: [
        {
          path: Routes.PASSWORD_CHANGED_CONFIRMATION,
          element: <Suspense fallback={fallback}><PasswordChangedConfirmationPage /></Suspense>,
        },
        {
          path: Routes.EMAIL_CHANGED_CONFIRMATION,
          element: <Suspense fallback={fallback}><EmailChangedConfirmationPage /></Suspense>,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.PAYMENT_SELECT,
          element: <Suspense fallback={fallback}><PaymentSelectPage /></Suspense>,
        },
      ],
    },
    {
      path: Routes.EMAIL_TEMPLATE,
      element: (
        <LayoutInternal
          appUrl={import.meta.env.VITE_APP_URL}
          appName={'app-name'}
          logo={`${import.meta.env.VITE_APP_URL}/assets/logo.png`}
        >
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab animi atque possimus, quaerat quasi tempore
          totam! Architecto assumenda cumque deleniti dolor expedita facilis illo minima nihil quidem soluta? Hic,
          laboriosam!
        </LayoutInternal>
      ),
    },
    {
      path: '*',
      element: <Navigate to={Routes.GYMS} />,
    },
  ])
);

export default router;
